import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";

import { Button, Drawer } from "@mui/material";
import { AsyncContent, RoleComponentGuard, SimpleHead } from "components";
import { useFormik } from "formik";
import { useActionHook, useDealParamsHooks, useFormatDateHook, useModals, useNavigationHooks } from "hooks";
import { IApiServiceError, usePickUserPermissions, useTaskManageActions, useUserRoleSelector } from "store";
import { IUpdateTaskReq, TaskFile } from "store/types";
import apiErrorsToFormik from "utils/api-errors-to-formik";
import { isRoleAnalyst } from "utils/user";

import { RoleName, taskPrioritiesObj, taskStatuses } from "../../../constants";
import { Attachments } from "../Attachments/Attachments";
import { FieldsSet, FieldsState } from "../Fields-set/FieldsSet";
import { Info } from "../Info/Info";
import { LinkedObject } from "../Linked-object/LinkedObject";
import { Menu } from "../Menu/Menu";

import "./Task.styles.scss";

const initialFields: FieldsState = {
  title: "",
  deal: null,
  assignee: null,
  status: { label: taskStatuses["open"], value: "open" },
  priority: null,
  due_date: null,
  description: null
};

interface Props {
  refetchTable?: () => void;
}

export const Task: React.FC<Props> = ({ refetchTable }) => {
  const { deleteModal } = useModals();
  const { task: taskId } = useDealParamsHooks();
  const { removeParams } = useNavigationHooks();
  const { getTaskAction, updateTaskAction, deleteTaskAction } = useTaskManageActions();
  const { action, loading, data } = useActionHook(getTaskAction);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [oldFiles, setOldFiles] = useState<TaskFile[]>([]);
  const formatDate = useFormatDateHook();
  const role = useUserRoleSelector();
  const taskManager = usePickUserPermissions("task_manager");

  const isViewerPermission = taskManager && taskManager === "viewer";

  const reinitilazedSate: FieldsState = useMemo(() => {
    if (!data) return initialFields;
    const { title, deal, assignee, status, priority, due_date, description, attachments } = data;
    if (attachments.length) {
      setOldFiles(attachments);
    }
    return {
      title: title,
      deal: deal,
      assignee: assignee,
      status: { value: status, label: taskStatuses[status] },
      priority: priority ? { label: taskPrioritiesObj[priority], value: priority } : null,
      due_date: due_date ? new Date(due_date).toISOString() : null,
      description: description || null
    };
  }, [data]);

  const { values, setFieldValue, errors, setErrors } = useFormik({
    initialValues: reinitilazedSate,
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: () => {}
  });

  const onCloseAndReset = useCallback(() => {
    setIsUpdating(false);
    setAttachments([]);
    setOldFiles([]);
    removeParams(["task"]);
  }, []);

  const onDelete = useCallback(async () => {
    if (!data) return;
    deleteModal({
      message: `Are you sure want to delete task: ${data.title}`,
      element: "Delete",
      action: async () => {
        await deleteTaskAction(data.id);
        onCloseAndReset();
      }
    });
  }, [data, deleteModal]);

  const onSubmit = useCallback(async () => {
    try {
      if (!data) return;
      setIsUpdating(true);

      const { title, description, assignee, due_date, status, priority, deal } = values;
      const requestData: IUpdateTaskReq = {
        id: data.id,
        title,
        files: [...attachments, ...oldFiles.map((item) => item.id)]
      };

      if (description && data.description !== description) {
        requestData.description = description;
      }

      if (assignee && data.assignee?.id !== assignee.id) {
        requestData.assignee_id = assignee.id;
      }

      const formatedDate = due_date ? formatDate({ date: due_date, format: "YYYY-MM-DD" }) : null;
      const formatedDueDate = data.due_date ? formatDate({ date: data.due_date, format: "YYYY-MM-DD" }) : null;
      if (formatedDate && formatedDueDate !== formatedDate) {
        requestData.due_date = formatedDate;
      }

      if (status && status?.value && data.status !== status?.value) {
        requestData.status = status.value;
      }

      if (priority && data.priority !== priority.value) {
        requestData.priority = priority.value;
      }

      if (data.deal?.id !== deal?.id) {
        requestData.deal_id = deal?.id || null;
      }

      await updateTaskAction(requestData);
      if (refetchTable) {
        refetchTable();
      }
      onCloseAndReset();
    } catch (error) {
      setErrors(apiErrorsToFormik(initialFields, (error as IApiServiceError).errors));
    } finally {
      setIsUpdating(false);
    }
  }, [data, values, attachments, oldFiles]);

  const addFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target?.files?.[0];
      if (file) {
        setAttachments([...attachments, file]);
      }
    },
    [attachments]
  );

  const removeFreshFile = useCallback(
    (date: number) => {
      setAttachments(attachments.filter((item) => item.lastModified !== date));
    },
    [attachments]
  );

  const removeOldFile = useCallback(
    (id: number) => {
      setOldFiles(oldFiles.filter((item) => item.id !== id));
    },
    [oldFiles]
  );

  useEffect(() => {
    if (taskId) {
      action(taskId);
    }
  }, [taskId]);

  return (
    <Drawer sx={{ ".MuiPaper-root": { borderRadius: "0 20px 20px 0" } }} onClose={onCloseAndReset} open={!!taskId}>
      <div className="task-drawer">
        <SimpleHead onIconClick={onCloseAndReset}>Task Details</SimpleHead>
        <AsyncContent spinnerProps={{ size: 150 }} loading={loading || isUpdating} className="body">
          <FieldsSet
            setFieldValue={setFieldValue}
            values={values}
            menu={
              <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermission}>
                <Menu onDelete={onDelete} />
              </RoleComponentGuard>
            }
            errors={errors}
            setErrors={setErrors}
            disabled={!isRoleAnalyst({ role, preventTV: true, preventUser: isViewerPermission })}
          />
          <LinkedObject linkedEntity={data?.linked_entity} dealId={data?.deal?.id} closeDrawer={onCloseAndReset} />
          <Info created={data?.created_at} creator={data?.creator} lastUpdated={data?.updated_at} />
          <Attachments
            onAddFile={addFile}
            freshItems={attachments}
            oldFiles={oldFiles}
            removeFreshFile={removeFreshFile}
            removeOldFile={removeOldFile}
          />
        </AsyncContent>
        <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermission}>
          <div className="footer">
            <Button variant="outlined" onClick={onCloseAndReset}>
              Cancel
            </Button>
            <Button disabled={isUpdating || !values.title} onClick={onSubmit} type="submit" variant="contained">
              Save
            </Button>
          </div>
        </RoleComponentGuard>
      </div>
    </Drawer>
  );
};
